import { ElementaryExchange, ExchangesResponse, IntermediateExchange } from 'hooks/data/datasets/exchanges/types';

export type ExchangeSection = 'input' | 'output';

/** An exchange present in one or both datasets, with the per-dataset amounts aligned by position. */
export type MergedExchange = {
  key: string;
  name: string;
  unit: string;
  group: string;
  amounts: [number | undefined, number | undefined];
};

export type ExchangeComparison = {
  inputs: MergedExchange[];
  outputs: MergedExchange[];
};

const INPUT_GROUPS = new Set(['From technosphere', 'From environment']);

export const getExchangeSection = (group: string): ExchangeSection => (INPUT_GROUPS.has(group) ? 'input' : 'output');

type NormalizedExchange = { key: string; name: string; unit: string; group: string; amount: number };

/*
 * Every match key is a natural key, so an exchange is identified the same way in every release. Spold ids
 * are release-scoped — `link.url` is concatenated straight into a dataset route — so keying on one
 * would silently pair unrelated activities that happen to share a number, which is worse than not
 * pairing them at all.
 *
 * - Technosphere: the supplying activity (`link.name` + `link.geography`) plus the product name and
 *   unit. All four matter: one supplier can feed several products, and the same product can arrive
 *   in different units.
 * - Unlinked intermediate exchanges — an activity's own reference product and by-products, which
 *   have no supplier to point at — are identified by product name and unit alone.
 * - Elementary: name + compartment + sub-compartment + unit.
 *
 * A supplier renamed between releases therefore fails to match and shows as two unmatched rows.
 * That is the safe direction to fail: a missing pair is visible in the table, a wrong pair is not.
 */
const normalize = (exchanges?: ExchangesResponse): NormalizedExchange[] => {
  if (!exchanges) return [];

  const intermediate = (exchanges.intermediateExchange ?? []).map((exc: IntermediateExchange) => ({
    key: exc.link ? `int|${exc.link.name}|${exc.link.geography}|${exc.name}|${exc.unit}` : `int|${exc.name}|${exc.unit}`,
    name: exc.name,
    unit: exc.unit,
    group: exc.group,
    amount: exc.amount,
  }));

  const elementary = (exchanges.elementaryExchange ?? []).map((exc: ElementaryExchange) => ({
    key: `ele|${exc.name}|${exc.compartment}|${exc.sub_compartment}|${exc.unit}`,
    name: exc.name,
    unit: exc.unit,
    group: exc.group,
    amount: exc.amount,
  }));

  return [...intermediate, ...elementary];
};

/** Merges the exchanges of two datasets into aligned input/output rows, keyed by matching exchange. */
export const buildExchangeComparison = (a?: ExchangesResponse, b?: ExchangesResponse): ExchangeComparison => {
  const merged = new Map<string, MergedExchange>();

  const add = (exchange: NormalizedExchange, index: 0 | 1) => {
    const existing = merged.get(exchange.key);
    if (existing) {
      existing.amounts[index] = (existing.amounts[index] ?? 0) + exchange.amount;
      return;
    }
    const amounts: [number | undefined, number | undefined] = [undefined, undefined];
    amounts[index] = exchange.amount;
    merged.set(exchange.key, { key: exchange.key, name: exchange.name, unit: exchange.unit, group: exchange.group, amounts });
  };

  normalize(a).forEach((exchange) => add(exchange, 0));
  normalize(b).forEach((exchange) => add(exchange, 1));

  const all = [...merged.values()].sort((x, y) => x.name.localeCompare(y.name));

  return {
    inputs: all.filter((exchange) => getExchangeSection(exchange.group) === 'input'),
    outputs: all.filter((exchange) => getExchangeSection(exchange.group) === 'output'),
  };
};

export const isCommonExchange = (exchange: MergedExchange): boolean =>
  exchange.amounts[0] !== undefined && exchange.amounts[1] !== undefined;

/** Why a table (or the whole tab) has nothing to show, or `null` when it does. */
export type ExchangesEmptyReason = 'no-exchanges' | 'no-common-exchanges';

/**
 * The two cases need different wording and must not be confused: only `no-common-exchanges` may
 * offer "turn the filter off to see all N", because that is the one case where something is actually
 * being filtered out. With no exchanges at all, that hint would point at an empty list.
 */
export const getEmptyReason = (totalCount: number, visibleCount: number): ExchangesEmptyReason | null => {
  if (totalCount === 0) return 'no-exchanges';
  if (visibleCount === 0) return 'no-common-exchanges';

  return null;
};
